import { HolderOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, message } from 'antd';
import { useRef, useState } from 'react';
import {
  SortEndHandler,
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import { officialTypeId, useTypeLabel } from '../hooks/useLabel';
import { arrayMoveImmutable } from 'array-move';
import { updateTodoTypeList } from '../service';
import { useDebounceFn } from 'ahooks';
import isEqual from 'react-fast-compare';
import styled from '@emotion/styled';
import { filter } from 'lodash-es';

const topMaxCount = 999999;

const SortDragger = SortableHandle(() => <Button type="text" icon={<HolderOutlined />} />);

const SortElement = SortableElement(({ children }: { children: JSX.Element }) => children);

const SortList = SortableContainer(({ children }: { children: JSX.Element }) => children);

export interface TypeOrderProps {
  typeList: Todo.TypeList;
  onTypeListChange: (typeList: Todo.TypeList) => void;
  customTypeLabelMap: Record<string, string>;
  taskId: string;
}

function TypeOrder(props: TypeOrderProps) {
  const { typeList: typeListProp, onTypeListChange, customTypeLabelMap, taskId } = props;

  const { getTypeLabel, t } = useTypeLabel();

  const [typeList, setTypeList] = useState(typeListProp);

  const { typeOrder, otherType } = typeList;

  const list = filter(
    typeOrder.map((type, index) => {
      const label = officialTypeId.includes(type) ? getTypeLabel(type) : customTypeLabelMap[type];
      if (!label) return undefined!;

      return (
        <SortElement key={type} index={index}>
          <div className="max-w-[360px] truncate">
            <SortDragger />
            {label}
          </div>
        </SortElement>
      );
    }),
    Boolean,
  );

  const others = otherType.map((type, index) => {
    const label = officialTypeId.includes(type) ? getTypeLabel(type) : customTypeLabelMap[type];

    return (
      <SortElement key={type} index={index + typeOrder.length + 1}>
        <div>
          <SortDragger />
          {label}
        </div>
      </SortElement>
    );
  });

  const flagRef = useRef(0);

  const { run: updateOrder } = useDebounceFn(
    (next: Todo.TypeList) => {
      flagRef.current += 1;

      const currentFlag = flagRef.current;

      updateTodoTypeList({ typeOrder: next.typeOrder, taskId: taskId })
        .then(() => {
          if (flagRef.current === currentFlag) {
            onTypeListChange(next);
          }
        })
        .catch(() => {
          if (flagRef.current === currentFlag) {
            setTypeList(typeListProp);

            message.error(t('operation.saveFailed'));
          }
        });
    },
    { wait: 500 },
  );

  function reDistribute<T>(orderList: T[], otherList: T[]) {
    return {
      orderList: orderList.length <= topMaxCount ? orderList : orderList.slice(0, topMaxCount),
      otherList:
        orderList.length <= topMaxCount
          ? otherList
          : [...orderList.slice(topMaxCount), ...otherList],
    };
  }

  const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
    const holder = Symbol('holder');

    const nextList = arrayMoveImmutable([...typeOrder, holder, ...otherType], oldIndex, newIndex);

    const holderIndex = nextList.indexOf(holder);

    const { orderList, otherList } = reDistribute(
      nextList.slice(0, holderIndex) as Todo.Type[],
      nextList.slice(holderIndex + 1) as Todo.Type[],
    );

    const next = {
      typeOrder: orderList,
      otherType: otherList,
    };

    setTypeList(next);

    if (!isEqual(typeOrder, next.typeOrder)) {
      updateOrder(next);
    }
  };

  const { orderList, otherList } = reDistribute(list, others);

  return (
    <SortList useDragHandle onSortEnd={onSortEnd} helperClass={draggingClassName}>
      <div className="max-h-[660px] overflow-auto">
        <Title>
          {`${t('marketing.置顶')}(${typeOrder.length}/${typeOrder.length + otherType.length})`}
        </Title>
        {orderList}
        <SortElement index={typeOrder.length}>
          <Title>{t('aide.其他')}</Title>
        </SortElement>
        {otherList}
      </div>
    </SortList>
  );
}

const draggingClassName = css`
  background: #fafafa;
  opacity: 0.7;
  visibility: visible;
  z-index: 100000;
  pointer-events: auto !important;
`;

const Title = styled.div`
  font-size: 12px;
  color: #84868c;
`;

export default TypeOrder;
